// Monetisation → Revenue, Customers, Events and a player's Subscription tab (contracts/revenue.md §4, §5).
// Every route is GET and open to every admin role, like GET /paywall.

import { keepPreviousData, useInfiniteQuery, useQuery } from "@tanstack/react-query";
import type { KindGroup, RevenueEnvFilter, RevenueStoreFilter } from "../lib/revenue";
import { request } from "./client";
import { Customers, PlayerRevenue, RevenueEvents, RevenueOverview } from "./revenueSchemas";

const enc = encodeURIComponent;
export const REVENUE_PAGE_SIZE = 50;

export interface RevenueScope {
  env: RevenueEnvFilter;
  store: RevenueStoreFilter;
}

export interface OverviewQuery extends RevenueScope {
  from: string;
  to: string;
}

export const CUSTOMER_SORTS = ["recent", "next_charge", "spent"] as const;
export type CustomerSort = (typeof CUSTOMER_SORTS)[number];

export interface CustomersQuery extends RevenueScope {
  /** One status or several, comma-separated; `active` also matches cancelled and grace. */
  status: string | null;
  plan: string | null;
  q: string;
  sort: CustomerSort;
}

export interface EventsQuery extends RevenueScope {
  from: string;
  to: string;
  /** A kind, several comma-separated, or a group (charges, trials, churn); null for every kind. */
  kind: string | null;
}

/** The `kind` query for a group and an optional specific kind (the kind wins). */
export const kindQuery = (group: KindGroup, kind: string | null) => kind || (group === "all" ? null : group);

export const revenueApi = {
  overview: (q: OverviewQuery, signal?: AbortSignal) =>
    request(RevenueOverview, "GET", "/revenue/overview", { query: { from: q.from, to: q.to, env: q.env, store: q.store }, signal }),
  customers: (q: CustomersQuery, cursor?: string | null, signal?: AbortSignal, limit = REVENUE_PAGE_SIZE) =>
    request(Customers, "GET", "/revenue/customers", {
      query: { env: q.env, store: q.store, status: q.status, plan: q.plan, q: q.q.trim(), sort: q.sort, cursor, limit },
      signal,
    }),
  events: (q: EventsQuery, cursor?: string | null, signal?: AbortSignal, limit = REVENUE_PAGE_SIZE) =>
    request(RevenueEvents, "GET", "/revenue/events", { query: { from: q.from, to: q.to, env: q.env, store: q.store, kind: q.kind, cursor, limit }, signal }),
  player: (id: string, signal?: AbortSignal) => request(PlayerRevenue, "GET", `/revenue/players/${enc(id)}`, { signal }),
};

export const revenueKeys = {
  all: ["revenue"] as const,
  overview: (q: OverviewQuery) => ["revenue", "overview", q] as const,
  customers: (q: CustomersQuery) => ["revenue", "customers", q] as const,
  events: (q: EventsQuery) => ["revenue", "events", q] as const,
  player: (id: string) => ["revenue", "player", id] as const,
};

export const useRevenueOverview = (q: OverviewQuery) =>
  useQuery({ queryKey: revenueKeys.overview(q), queryFn: ({ signal }) => revenueApi.overview(q, signal), placeholderData: keepPreviousData });

export const useCustomers = (q: CustomersQuery) =>
  useInfiniteQuery({
    queryKey: revenueKeys.customers(q),
    queryFn: ({ pageParam, signal }) => revenueApi.customers(q, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
  });

export const useRevenueEvents = (q: EventsQuery) =>
  useInfiniteQuery({
    queryKey: revenueKeys.events(q),
    queryFn: ({ pageParam, signal }) => revenueApi.events(q, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
  });

export const usePlayerRevenue = (id: string | undefined) =>
  useQuery({ queryKey: revenueKeys.player(id ?? ""), queryFn: ({ signal }) => revenueApi.player(id!, signal), enabled: Boolean(id) });
